import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Database,
    DownloadSimple,
    ArrowClockwise,
    Clock,
    ShieldCheck,
    ArrowCounterClockwise,
    Buildings,
    Trash,
    ListChecks,
    Info,
    CheckCircle,
    XCircle,
    HardDrives,
} from "@phosphor-icons/react";

const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const KIND_STYLES = {
    manual: "bg-violet-100 text-violet-700",
    auto: "bg-emerald-100 text-emerald-700",
    safety: "bg-amber-100 text-amber-800",
};

const KindPill = ({ kind }) => (
    <span
        data-testid={`kind-pill-${kind}`}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
            KIND_STYLES[kind] || "bg-slate-100 text-slate-700"
        }`}
    >
        {kind || "—"}
    </span>
);

const StatusPill = ({ status }) => (
    <span
        data-testid={`status-pill-${status}`}
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            status === "success"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-rose-50 text-rose-700"
        }`}
    >
        {status === "success" ? <CheckCircle size={11} weight="fill" /> : <XCircle size={11} weight="fill" />}
        {status === "success" ? "Successful" : "Failed"}
    </span>
);

export default function SuperBackupCenter() {
    const [companies, setCompanies] = useState([]);
    const [keepCount, setKeepCount] = useState(30);
    const [selectedCid, setSelectedCid] = useState("");
    const [backups, setBackups] = useState([]);
    const [recoveryLog, setRecoveryLog] = useState([]);
    const [tab, setTab] = useState("backups"); // backups | log
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingBackups, setLoadingBackups] = useState(false);
    const [creating, setCreating] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewMeta, setPreviewMeta] = useState(null);
    const [restoreOpen, setRestoreOpen] = useState(false);
    const [restoreMeta, setRestoreMeta] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [restoring, setRestoring] = useState(false);

    const loadCompanies = useCallback(async () => {
        setLoadingCompanies(true);
        try {
            const { data } = await api.get("/super/backup-center/companies");
            setCompanies(data.companies || []);
            setKeepCount(data.keep_per_company || 30);
            if (!selectedCid && (data.companies || []).length > 0) {
                setSelectedCid(data.companies[0].id);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoadingCompanies(false);
        }
    }, [selectedCid]);

    const loadBackups = useCallback(async (cid) => {
        if (!cid) return;
        setLoadingBackups(true);
        try {
            const { data } = await api.get("/super/backup-center/backups", { params: { company_id: cid } });
            setBackups(data.backups || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoadingBackups(false);
        }
    }, []);

    const loadRecoveryLog = useCallback(async (cid) => {
        try {
            const { data } = await api.get("/super/backup-center/recovery-log", {
                params: cid ? { company_id: cid } : {},
            });
            setRecoveryLog(data.entries || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    useEffect(() => {
        if (selectedCid) {
            loadBackups(selectedCid);
            loadRecoveryLog(selectedCid);
        }
    }, [selectedCid, loadBackups, loadRecoveryLog]);

    const selectedCompany = useMemo(
        () => companies.find((c) => c.id === selectedCid) || null,
        [companies, selectedCid],
    );

    const createNow = async () => {
        if (!selectedCid) {
            toast.error("Choose a company first.");
            return;
        }
        setCreating(true);
        try {
            const { data } = await api.post(`/super/backup-center/companies/${selectedCid}/create`);
            const b = data.backup || {};
            toast.success(
                `Backup created · ${b.total_rows} rows · ${b.gridfs_files_count} files · ${fmtBytes(b.size_bytes)}`,
                { duration: 6000 },
            );
            await Promise.all([loadCompanies(), loadBackups(selectedCid), loadRecoveryLog(selectedCid)]);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCreating(false);
        }
    };

    const download = async (b) => {
        try {
            const token = localStorage.getItem("micm_token");
            const res = await fetch(`${API_BASE}/super/backup-center/backups/${b.id}/download`, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = b.file_name || `backup-${b.id}.json`;
            a.click();
            URL.revokeObjectURL(url);
            await loadRecoveryLog(selectedCid);
            toast.success(`Downloaded ${b.file_name}`);
        } catch (e) {
            toast.error(`Download failed · ${e.message || e}`);
        }
    };

    const openPreview = async (b) => {
        setPreviewMeta(b);
        setPreviewOpen(true);
        try {
            const { data } = await api.get(`/super/backup-center/backups/${b.id}`);
            setPreviewMeta(data.backup || b);
            await loadRecoveryLog(selectedCid);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const openRestore = (b) => {
        setRestoreMeta(b);
        setConfirmText("");
        setRestoreOpen(true);
    };

    const doRestore = async () => {
        if (!restoreMeta) return;
        if (confirmText.trim() !== "RESTORE BACKUP") {
            toast.error('Type "RESTORE BACKUP" exactly to confirm.');
            return;
        }
        setRestoring(true);
        try {
            const { data } = await api.post(
                `/super/backup-center/backups/${restoreMeta.id}/restore`,
                { confirm: confirmText.trim(), company_id: restoreMeta.company_id },
            );
            const counts = data.restored_counts || {};
            const totalRestored = Object.values(counts).reduce((s, n) => s + Number(n || 0), 0);
            toast.success(
                `Restored · ${totalRestored} rows · ${data.gridfs_files_restored} files · safety backup ${
                    data.safety_backup?.id ? "created" : "skipped"
                }`,
                { duration: 8000 },
            );
            setRestoreOpen(false);
            setRestoreMeta(null);
            setConfirmText("");
            await Promise.all([loadCompanies(), loadBackups(selectedCid), loadRecoveryLog(selectedCid)]);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRestoring(false);
        }
    };

    const deleteBackup = async (b) => {
        if (!window.confirm(`Permanently delete backup "${b.file_name}"?\nThis cannot be undone.`)) return;
        try {
            await api.delete(`/super/backup-center/backups/${b.id}`);
            toast.success("Backup deleted");
            await Promise.all([loadCompanies(), loadBackups(selectedCid), loadRecoveryLog(selectedCid)]);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="super-backup-center" className="space-y-6">
            {/* Heading */}
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-violet-700">
                        <ShieldCheck size={14} weight="fill" /> Super User only
                    </div>
                    <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">
                        Backup Center
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-slate-500">
                        Full per-company snapshots (rows + slip files). Auto-daily snapshots kept last {keepCount} per company. Manual and safety backups are kept forever. Cross-company restores are blocked.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={loadCompanies} data-testid="bc-refresh">
                        <ArrowClockwise size={14} className="mr-1.5" /> Refresh
                    </Button>
                    <Button
                        size="sm"
                        onClick={createNow}
                        disabled={creating || !selectedCid}
                        data-testid="bc-create-now"
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                        <ShieldCheck size={14} className="mr-1.5" />
                        {creating ? "Creating…" : "Create Backup Now"}
                    </Button>
                </div>
            </div>

            {/* Company chooser */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                        <Buildings size={14} /> Companies
                    </div>
                    <div className="text-[11px] text-slate-500">
                        {loadingCompanies ? "Loading…" : `${companies.length} total`}
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    {companies.map((c) => {
                        const isActive = c.id === selectedCid;
                        return (
                            <button
                                key={c.id}
                                data-testid={`bc-company-${c.id}`}
                                onClick={() => setSelectedCid(c.id)}
                                className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition ${
                                    isActive
                                        ? "border-violet-400 bg-violet-50/60 shadow-sm"
                                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                }`}
                            >
                                <div className="flex w-full items-center justify-between">
                                    <div className="truncate text-sm font-semibold text-slate-900">{c.name}</div>
                                    {isActive && (
                                        <CheckCircle size={14} weight="fill" className="text-violet-600" />
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                                    <span className="inline-flex items-center gap-1">
                                        <Database size={11} /> {c.backup_count} backups
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <HardDrives size={11} /> {fmtBytes(c.total_size_bytes)}
                                    </span>
                                </div>
                                <div className="text-[10px] text-slate-400">
                                    Last: {c.last_backup_at ? fmtWhen(c.last_backup_at) : "never"}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {selectedCompany && (
                <>
                    {/* KPI tiles for the selected company */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <KpiTile label="Total backups" value={selectedCompany.backup_count} icon={<Database size={13} />} tone="slate" testId="kpi-total-backups" />
                        <KpiTile label="Auto (rolling)" value={`${selectedCompany.auto_count} / ${keepCount}`} icon={<Clock size={13} />} tone="emerald" testId="kpi-auto-count" />
                        <KpiTile label="Manual + safety" value={selectedCompany.manual_count + selectedCompany.safety_count} icon={<ShieldCheck size={13} />} tone="violet" testId="kpi-manual-count" />
                        <KpiTile label="Disk usage" value={fmtBytes(selectedCompany.total_size_bytes)} icon={<HardDrives size={13} />} tone="amber" testId="kpi-disk-usage" />
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 border-b border-slate-200">
                        <TabBtn active={tab === "backups"} onClick={() => setTab("backups")} testId="bc-tab-backups">
                            <Database size={13} className="mr-1.5" /> Backups
                        </TabBtn>
                        <TabBtn active={tab === "log"} onClick={() => setTab("log")} testId="bc-tab-log">
                            <ListChecks size={13} className="mr-1.5" /> Recovery Log
                        </TabBtn>
                    </div>

                    {tab === "backups" && (
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="grid grid-cols-12 gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                <div className="col-span-3">File</div>
                                <div className="col-span-2">Created</div>
                                <div className="col-span-1">Kind</div>
                                <div className="col-span-1">Status</div>
                                <div className="col-span-2">Records · Files</div>
                                <div className="col-span-1">Size</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>
                            {loadingBackups && backups.length === 0 && (
                                <div className="px-6 py-14 text-center text-sm text-slate-400">Loading…</div>
                            )}
                            {!loadingBackups && backups.length === 0 && (
                                <div className="flex flex-col items-center gap-2 px-6 py-14 text-center text-slate-400">
                                    <Database size={28} />
                                    <div className="text-sm">
                                        No backups for this company yet — click <b>Create Backup Now</b>.
                                    </div>
                                </div>
                            )}
                            <ul>
                                {backups.map((b) => (
                                    <li
                                        key={b.id}
                                        data-testid={`bc-row-${b.id}`}
                                        className="grid grid-cols-12 items-center gap-2 border-t border-slate-100 px-4 py-3 first:border-0 hover:bg-slate-50"
                                    >
                                        <div className="col-span-3 min-w-0">
                                            <div className="truncate font-mono text-xs font-medium text-slate-900" title={b.file_name}>
                                                {b.file_name}
                                            </div>
                                            <div className="mt-0.5 truncate text-[10px] text-slate-400" title={b.company_name}>
                                                {b.company_name}
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-xs text-slate-600">
                                            <div>{fmtWhen(b.created_at)}</div>
                                            <div className="text-[10px] text-slate-400">by {b.created_by_email}</div>
                                        </div>
                                        <div className="col-span-1"><KindPill kind={b.kind} /></div>
                                        <div className="col-span-1"><StatusPill status={b.status} /></div>
                                        <div className="col-span-2 text-xs text-slate-700">
                                            <div>{b.total_rows?.toLocaleString?.() ?? b.total_rows} rows</div>
                                            <div className="text-[10px] text-slate-500">{b.gridfs_files_count || 0} slip file(s)</div>
                                        </div>
                                        <div className="col-span-1 text-xs text-slate-700">{fmtBytes(b.size_bytes)}</div>
                                        <div className="col-span-2 flex justify-end gap-1">
                                            <Button variant="outline" size="sm" onClick={() => openPreview(b)} data-testid={`bc-preview-${b.id}`}>
                                                <Info size={13} className="mr-1" /> Preview
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => download(b)} data-testid={`bc-download-${b.id}`}>
                                                <DownloadSimple size={13} className="mr-1" /> Download
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => openRestore(b)}
                                                data-testid={`bc-restore-${b.id}`}
                                                className="bg-emerald-600 text-white hover:bg-emerald-700"
                                            >
                                                <ArrowCounterClockwise size={13} className="mr-1" /> Restore
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => deleteBackup(b)}
                                                data-testid={`bc-delete-${b.id}`}
                                                className="border-rose-200 text-rose-600 hover:bg-rose-50"
                                            >
                                                <Trash size={13} />
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {tab === "log" && (
                        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="grid grid-cols-12 gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                <div className="col-span-2">When</div>
                                <div className="col-span-1">Action</div>
                                <div className="col-span-2">Kind</div>
                                <div className="col-span-3">By</div>
                                <div className="col-span-4">Note</div>
                            </div>
                            {recoveryLog.length === 0 && (
                                <div className="px-6 py-12 text-center text-sm text-slate-400">No recovery log entries yet.</div>
                            )}
                            <ul data-testid="bc-recovery-log">
                                {recoveryLog.map((r) => (
                                    <li key={r.id} className="grid grid-cols-12 items-center gap-2 border-t border-slate-100 px-4 py-2 first:border-0 hover:bg-slate-50">
                                        <div className="col-span-2 text-xs text-slate-700">{fmtWhen(r.at)}</div>
                                        <div className="col-span-1">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                                    r.action === "restore"
                                                        ? "bg-rose-100 text-rose-700"
                                                        : r.action === "download"
                                                        ? "bg-sky-100 text-sky-700"
                                                        : r.action === "delete"
                                                        ? "bg-slate-200 text-slate-700"
                                                        : "bg-violet-100 text-violet-700"
                                                }`}
                                            >
                                                {r.action}
                                            </span>
                                        </div>
                                        <div className="col-span-2"><KindPill kind={r.kind} /></div>
                                        <div className="col-span-3 truncate text-xs text-slate-700">{r.su_email}</div>
                                        <div className="col-span-4 truncate text-[11px] text-slate-500" title={r.note}>{r.note}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-2xl" data-testid="bc-preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Info size={18} /> Backup Preview
                        </DialogTitle>
                        <DialogDescription>
                            Full metadata for the selected backup. No live data has been touched.
                        </DialogDescription>
                    </DialogHeader>
                    {previewMeta && (
                        <div className="space-y-3 text-sm">
                            <PreviewRow label="Company" value={previewMeta.company_name} />
                            <PreviewRow label="Backup date & time" value={fmtWhen(previewMeta.created_at)} />
                            <PreviewRow label="Kind" value={<KindPill kind={previewMeta.kind} />} />
                            <PreviewRow label="Status" value={<StatusPill status={previewMeta.status} />} />
                            <PreviewRow label="File" valueMono value={previewMeta.file_name} />
                            <PreviewRow label="Size on disk" value={fmtBytes(previewMeta.size_bytes)} />
                            <PreviewRow label="Total records" value={previewMeta.total_rows?.toLocaleString?.() ?? previewMeta.total_rows} />
                            <PreviewRow label="Slip files" value={previewMeta.gridfs_files_count} />
                            <PreviewRow label="Created by" value={previewMeta.created_by_email} />
                            {previewMeta.note && <PreviewRow label="Note" value={previewMeta.note} />}
                            {previewMeta.restored_at && (
                                <PreviewRow
                                    label="Last restored"
                                    value={`${fmtWhen(previewMeta.restored_at)} · by ${previewMeta.restored_by || "—"}`}
                                />
                            )}
                            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    Records per collection
                                </div>
                                <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700">
                                    {Object.entries(previewMeta.counts || {}).map(([k, v]) => (
                                        <div key={k} className="flex justify-between">
                                            <span className="text-slate-500">{k}</span>
                                            <span className="font-semibold">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="bc-preview-close">
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Restore dialog */}
            <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
                <DialogContent className="max-w-2xl" data-testid="bc-restore-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-rose-700">
                            <ArrowCounterClockwise size={18} /> Restore Backup
                        </DialogTitle>
                        <DialogDescription>
                            Full overwrite of the target company's live data. A safety backup of the current state is created automatically before the restore begins.
                        </DialogDescription>
                    </DialogHeader>
                    {restoreMeta && (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800">
                                <div><b>Company:</b> {restoreMeta.company_name}</div>
                                <div><b>Backup date:</b> {fmtWhen(restoreMeta.created_at)}</div>
                                <div>
                                    <b>Records:</b> {restoreMeta.total_rows?.toLocaleString?.() ?? restoreMeta.total_rows} · {restoreMeta.gridfs_files_count || 0} slip file(s) · {fmtBytes(restoreMeta.size_bytes)}
                                </div>
                                <div><b>File:</b> <span className="font-mono">{restoreMeta.file_name}</span></div>
                            </div>
                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-800">
                                <b>Danger:</b> Every party, transaction, slip and file currently in "{restoreMeta.company_name}" will be replaced with the contents of this backup. Data from other companies is not touched.
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    Type <span className="font-mono text-rose-700">RESTORE BACKUP</span> to confirm
                                </label>
                                <Input
                                    autoFocus
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder="RESTORE BACKUP"
                                    data-testid="bc-restore-confirm-input"
                                    className="mt-1 font-mono"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRestoreOpen(false)} data-testid="bc-restore-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={doRestore}
                            disabled={restoring || confirmText.trim() !== "RESTORE BACKUP"}
                            data-testid="bc-restore-submit"
                            className="bg-rose-600 text-white hover:bg-rose-700"
                        >
                            {restoring ? "Restoring…" : "Restore Now"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function KpiTile({ label, value, icon, tone, testId }) {
    const tones = {
        slate: "border-slate-200 bg-white",
        emerald: "border-emerald-100 bg-emerald-50/40",
        violet: "border-violet-100 bg-violet-50/40",
        amber: "border-amber-100 bg-amber-50/40",
    };
    return (
        <div data-testid={testId} className={`rounded-xl border ${tones[tone] || tones.slate} p-3 shadow-sm`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
                {icon} {label}
            </div>
            <div className="mt-1 font-display text-lg font-bold text-slate-900">{value}</div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testId }) {
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`inline-flex items-center border-b-2 px-3 py-2 text-xs font-semibold transition ${
                active
                    ? "border-violet-600 text-violet-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
        >
            {children}
        </button>
    );
}

function PreviewRow({ label, value, valueMono }) {
    return (
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-2 last:border-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`text-right text-slate-800 ${valueMono ? "font-mono text-xs" : "text-sm"}`}>{value ?? "—"}</div>
        </div>
    );
}
